import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
    AttachmentType,
    CacheService,
    ClockService,
    Complaint,
    ComplaintData,
    ComplaintPosition,
    ComplaintsDocumentTypes,
    DocumentAttachment,
} from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { ConfigService, core } from '@icc/shared';
import { ComplaintPositionConfigurationSelectComponent } from '../complaint-position-configuration-select/complaint-position-configuration-select.component';
import { ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'icc-complaint-position-edit',
    templateUrl: './complaint-position-edit.component.html',
    styleUrls: ['./complaint-position-edit.component.scss'],
})
export class ComplaintPositionEditComponent implements OnInit {
    @Input() complaintPosition!: ComplaintPosition;
    @Input() complaint!: Complaint;
    @Input() complaintPositionAttachments: DocumentAttachment[] = [];
    @Input() showRemoveButton = true;
    @Input() b2cForm = false;
    @Input() index = 0;
    @Input() showDetails = true;
    @Input() positionsCount = 0;
    @Input() isB2bComplaintForm = false;
    @Output() removeComplaintPosition = new EventEmitter<number>();
    @Output() addAttachmentToPosition = new EventEmitter<DocumentAttachment>();
    @Output() removeAttachmentToPosition = new EventEmitter<string>();
    data: ComplaintData = {} as ComplaintData;
    @ViewChild(ComplaintPositionConfigurationSelectComponent) configSelectComponent: ComplaintPositionConfigurationSelectComponent;

    additionalPhotos: DocumentAttachment[] = [];
    additionalAttachments: DocumentAttachment[] = [];

    additionalPhotoDescription = '';
    attachmentDescription = '';

    showFields = {
        serialNumber: false,
        orderNumber: false,
        orderPositionNumber: false,
        invoiceNumber: false,
    };
    wholeProductPhotoPreview: string;
    closeUpPhotoPreview: string;

    documentTypes = ComplaintsDocumentTypes;

    today = new Date().toISOString().slice(0, 10);

    closeUpPhotoError: string | null = null;
    wholeProductPhotoError: string | null = null;
    additionalPhotoError: string | null = null;
    attachmentError: string | null = null;

    constructor(
        private complaintService: ComplaintService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private configService: ConfigService,
    ) {
        this.data = this.complaintService.data;
    }

    ngOnInit(): void {
        this.onComplaintDocumentTypeChange(this.complaintPosition.complaintDocumentType ?? '');
        this.additionalPhotos = this.getAttachments('additionalPhoto') ?? [];
        this.additionalAttachments = this.getAttachments('attachment') ? this.getAttachments('attachment') : [];
        if (this.b2cForm) {
            if (this.getAttachment('wholeProductPhoto')) {
                if (this.complaintService.cachedAttachments.has(this.getAttachment('wholeProductPhoto')?.uuid)) {
                    this.wholeProductPhotoPreview = this.complaintService.cachedAttachments.get(this.getAttachment('wholeProductPhoto')?.uuid);
                }
            }
            if (this.getAttachment('closeUpPhoto')) {
                if (this.complaintService.cachedAttachments.has(this.getAttachment('closeUpPhoto')?.uuid)) {
                    this.closeUpPhotoPreview = this.complaintService.cachedAttachments.get(this.getAttachment('closeUpPhoto')?.uuid);
                }
            }
        } else {
            this.wholeProductPhotoPreview = this.getAttachment('wholeProductPhoto') ? '/complaint_attachments/' + this.getAttachment('wholeProductPhoto')?.uuid : '';
            this.closeUpPhotoPreview = this.getAttachment('closeUpPhoto') ? '/complaint_attachments/' + this.getAttachment('closeUpPhoto')?.uuid : '';
        }
    }

    onComplaintProductTypeChange(event: number) {
        if (this.complaintPosition) {
            this.complaintPosition.complaintProductTypeId = Number(event);
            const complaintProductType = this.data.complaintProductTypes.find(
                (cpt) => cpt.id === Number(event)
            );
            this.complaintPosition.complaintProductType = complaintProductType?.name ?? '';
            this.complaintPosition.complaintDocumentType =
                complaintProductType?.defaultDocumentType;
            if (this.complaintPosition.complaintDocumentType) {
                this.onComplaintDocumentTypeChange(this.complaintPosition.complaintDocumentType);
            }
            if(this.configSelectComponent) {
                this.configSelectComponent.onChangeParentOption(this.complaintPosition.configuration);
            }
        }
    }

    onComplaintDocumentTypeChange(event: string) {
        switch (event) {
            case 'serialNumber':
                this.showFields = {
                    serialNumber: true,
                    orderNumber: false,
                    orderPositionNumber: false,
                    invoiceNumber: false,
                };
                this.complaintPosition.complaintDocumentType = 'serialNumber';
                break;
            case 'orderNumber':
                this.showFields = {
                    serialNumber: false,
                    orderNumber: true,
                    orderPositionNumber: true,
                    invoiceNumber: false,
                };
                this.complaintPosition.complaintDocumentType = 'orderNumber';
                break;
            case 'invoiceNumber':
                this.showFields = {
                    serialNumber: false,
                    orderNumber: false,
                    orderPositionNumber: false,
                    invoiceNumber: true,
                };
                this.complaintPosition.complaintDocumentType = 'invoiceNumber';
                break;
        }
    }

    async onAddAttachment(files: FileList, type: DocumentAttachment['attachmentType']) {
        const maxSizeInMB = 7;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        const maxFiles = 5;

        this.closeUpPhotoError = null;
        this.wholeProductPhotoError = null;
        this.additionalPhotoError = null;
        this.attachmentError = null;

        if (files[0]) {
            if (files[0].size > maxSizeInBytes) {
                const errorMessage = this.translateService.instant('COMPLAINT|Plik jest zbyt duży. Maksymalna wielkość to {maxSizeInMB}MB.', { maxSizeInMB });
                if (type === 'closeUpPhoto') {
                    this.closeUpPhotoError = errorMessage;
                    (document.getElementById('closeUpPhoto' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                } else if (type === 'wholeProductPhoto') {
                    this.wholeProductPhotoError = errorMessage;
                    (document.getElementById('wholeProductPhoto' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                } else if (type === 'additionalPhoto') {
                    this.additionalPhotoError = errorMessage;
                    (document.getElementById('additionalPhoto' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                } else if (type === 'attachment') {
                    this.attachmentError = errorMessage;
                    (document.getElementById('attachment' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                }
                return;
            }

            if (type === 'additionalPhoto' && this.additionalPhotos.length >= maxFiles) {
                this.additionalPhotoError = this.translateService.instant('COMPLAINT|Osiągnięto limit zdjęć. Możesz dodać maksymalnie {maxFiles} zdjęć.', { maxFiles });
                (document.getElementById('additionalPhoto' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                return;
            }

            if (type === 'attachment' && this.additionalAttachments.length >= maxFiles) {
                this.attachmentError = this.translateService.instant('COMPLAINT|Osiągnięto limit załączników. Możesz dodać maksymalnie {maxFiles} załączników.', { maxFiles });
                (document.getElementById('attachment' + this.complaintPosition.uuid) as HTMLInputElement).value = '';
                return;
            }

            const reader = new FileReader();
            const data = await core.blobToArrayBuffer(files[0]);
            const newAttachment: DocumentAttachment = {
                uuid: core.generateUUID(),
                type: 'complaint',
                documentId: this.complaint?.uuid,
                documentPositionId: this.complaintPosition?.uuid,
                fileType: files[0].type,
                created: new Date(),
                modified: new Date(),
                machineId: ClockService.getClock().timestamp.node(),
                name: files[0].name,
                data,
                attachmentType: type,
                description: '',
            };
            if (type !== 'attachment') {
                await this.complaintService.cacheAttachment(newAttachment.uuid, files[0]);
            }
            this.complaintPositionAttachments.push(newAttachment);
            reader.onload = (event: any) => {
                if (type === 'additionalPhoto') {
                    this.additionalPhotos.push(newAttachment);
                } else if (type === 'wholeProductPhoto') {
                    this.wholeProductPhotoPreview = event.target.result;
                } else if (type === 'closeUpPhoto') {
                    this.closeUpPhotoPreview = event.target.result;
                }
            }
            if (type === 'attachment') {
                this.additionalAttachments.push(newAttachment);
            }
            reader.readAsDataURL(files[0]);
            this.addAttachmentToPosition.emit(newAttachment);
        }
    }

    openInfo(
        type: 'purchaseDate' | 'serialNumber' | 'invoiceNumber' | 'orderNumber' | 'closeUpPhoto' | 'wholeProductPhoto' | 'description'
    ) {
        this.complaintService.openInfo(type, this.complaintPosition.complaintProductTypeId);
    }

    openDescriptionInfo() {
        this.complaintService.openDescriptionInfo(this.complaintPosition.complaintProductTypeId, +this.complaintService.getDeepestNestedCategoryId(this.complaintPosition.configuration));
    }

    getAttachmentName(type: DocumentAttachment['attachmentType']) {
        return this.getAttachment(type)?.name ?? null;
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.complaintPositionAttachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }

    getAttachments(type: AttachmentType): DocumentAttachment[] {
        return this.complaintPositionAttachments.filter(
            (attachment) => attachment.attachmentType === type
        );
    }

    remove() {
        this.removeComplaintPosition.emit();
    }

    getAdditionalPhotosAndAttachments() {
        return this.complaintPositionAttachments.filter(
            (el) => el.attachmentType === 'additionalPhoto' || el.attachmentType === 'attachment'
        );
    }

    removeAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = uuid
        ? this.complaintPositionAttachments.find(
              (el) => el.attachmentType === type && el.uuid === uuid
          )
        : this.complaintPositionAttachments.find(
              (el) => el.attachmentType === type
          );
        if (attachment) {
            if (type !== 'closeUpPhoto' && type !== 'wholeProductPhoto') {
                (document.getElementById(type + this.complaintPosition.uuid) as HTMLInputElement).value = '';
            }
            this.complaintPositionAttachments.splice(this.complaintPositionAttachments.findIndex(
                (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
            ), 1);
            this.removeAttachmentToPosition.emit(attachment.uuid);
            if(type === 'additionalPhoto') {
                this.additionalPhotos = this.additionalPhotos.filter((el) => el.uuid !== uuid);
            } else if (type === 'attachment') {
                this.additionalAttachments = this.additionalAttachments.filter((el) => el.uuid !== uuid);
            } else if (type === 'wholeProductPhoto') {
                this.wholeProductPhotoPreview = '';
            } else if (type === 'closeUpPhoto') {
                this.closeUpPhotoPreview = '';
            }
        }
    }

    async validateSerialNumber() {
        if (this.complaintPosition?.serialNumber && this.complaintPosition?.complaintDocumentType === 'serialNumber' && (!this.b2cForm || this.isB2bComplaintForm)) {
            const result = await this.complaintService.validateSerialNumber(this.complaintPosition.serialNumber, this.complaintPosition.uuid, this.complaint.documentNumber);
            if (this.isB2bComplaintForm || !this.b2cForm) {
                this.complaintPosition.invalidSerialNumber = !result?.valid ?? null;
                if (!this.complaintPosition.invalidSerialNumber) {
                    this.complaintPosition.distributorOrderNumber = result?.orderId ?? null;
                    this.complaintPosition.distributorOrderPositionNumber = result?.positionNo ?? null;
                }
            }
        }
    }

    switchShowDetails(showDetails: boolean) {
        this.showDetails = showDetails;
    }

    getIconName(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return 'pdf_file';
            case 'jpg':
            case 'jpeg':
            case 'png': return 'image_file';
            default: return 'file';
        }
    }

    getIconColor(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return '#E74856';
            case 'jpg':
            case 'jpeg':
            case 'png': return '#008231';
            default: return '#C19C00';
        }
    }

    getUserType() {
        return this.complaintService.getUserType();
    }
}
